// src/pages/PreviewCompetitionPage.jsx
import React, { useEffect, useState } from "react";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { app } from "../firebase";
import "./PreviewPage.css";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import BottomNav from "./BottomNav";
import { notificationService } from '../utils/notificationService';
import { useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import competitionManager from '../utils/competitionManager';
import RestrictionModal from '../components/RestrictionModal';
import { getImageUrl } from '../utils/imageUrls';
import { resolveAuthorName } from '../utils/authorLookup';

const db = getFirestore(app);

const PreviewCompetitionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    localPreview,                              // local image from the upload page
    permanentImageUrl: passedPermanentImageUrl, // kept for older flows
    imageDataUrl: passedImageDataUrl,           // kept for older flows
    imageUrl: passedImageUrl,                   // kept for older flows
    title: passedTitle,
    tags: passedTags,
    description: passedDesc,
    filePath,
  } = location.state || {};

  const [title, setTitle] = useState(passedTitle || "");
  const [tags, setTags] = useState(passedTags || []);
  const [description, setDescription] = useState(passedDesc || "");
  const [uid, setUid] = useState(null);
  const [posting, setPosting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [restriction, setRestriction] = useState(null);

  // Show the local image first. If it is gone (page refresh), fall back to the
  // public URL built from filePath. No signed URLs, no extra download.
  const firstChoice = localPreview || passedImageDataUrl || passedPermanentImageUrl || passedImageUrl || null;
  const [useFirst, setUseFirst] = useState(!!firstChoice);
  const publicSrc = filePath ? getImageUrl(filePath) : null;
  const previewSrc = useFirst && firstChoice ? firstChoice : publicSrc;

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) =>
      setUid(user ? user.uid : null)
    );
    return unsub;
  }, []);

  // Saved draft text (unchanged behavior). The old code also swapped the preview
  // image for users/{uid}.filePath here; that is removed because the image that
  // gets posted is always the filePath passed in from the upload page.
  useEffect(() => {
    if (!uid) return;

    (async () => {
      try {
        const userDocRef = doc(db, "users", uid);
        const userSnap = await getDoc(userDocRef);
        if (!userSnap.exists()) return;

        const data = userSnap.data();
        setTitle(data.savedTitle ?? title);
        setTags(passedTags || data.savedTags || tags);
        setDescription(data.savedDescription ?? description);
      } catch (err) {
        console.error("Failed to fetch draft:", err);
      }
    })();

  }, [uid]);

  // Payment gateway removed — idea now submits straight to Firestore,
  // same as it would after a successful payment before.
  const handleSubmit = async () => {
    const auth = getAuth(app);
    const user = auth.currentUser;

    if (!uid || !user) {
      notificationService.showWarning("Please sign in before proceeding.");
      return;
    }
    if (posting) return;

    // Safety-net checks in case this page was reached directly (e.g. via
    // browser back/forward), bypassing the checks on the New Post page.
    const time = competitionManager.getTimeRemaining();
    if (time.phase === 'break') {
      setRestriction({
        variant: 'break',
        stampText: 'On Break',
        message: `The competition is on a short break. Next competition starts in ${time.days}d ${time.hours}h ${time.minutes}m.`,
      });
      return;
    }

    const alreadyJoined = await competitionManager.isUserRegistered(uid);
    if (alreadyJoined) {
      setRestriction({
        variant: 'limit',
        stampText: 'One Entry Only',
        message: 'You can only join once per competition. Come back next round!',
      });
      return;
    }

    setPosting(true);

    try {
      // Never store the email address on a competition idea
      const authorName = await resolveAuthorName(user);

      const ideaData = {
        title: title.trim(),
        description: description.trim(),
        tags,
        imagePath: filePath,
        authorName,
        userId: uid,
        ownerId: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Dashboard.jsx hardcodes `where("status", "==", "paid")` for
        // competition ideas — keeping this value as 'paid' (even though
        // nothing is actually paid anymore) so entries still show up
        // without needing to touch the dashboard query too.
        status: 'paid',
        type: 'competition'
      };

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timed out. Network slow or blocked.")), 45000)
      );

      const docRef = await Promise.race([
        addDoc(collection(db, "competitionIdeas"), ideaData),
        timeoutPromise
      ]);

      // Mark this user as having entered the current competition cycle
      // so they can't submit a second idea until the next cycle starts.
      competitionManager.registerUser(uid).catch(err =>
        console.error("Failed to register user for competition:", err)
      );

      setPosting(false);
      navigate("/competition-success", {
        state: {
          ideaId: docRef.id
        }
      });

    } catch (err) {
      console.error("Error submitting idea:", err);
      notificationService.showError("Failed to submit your idea. Please try again.");
      setPosting(false);
    }
  };

  return (
    <div className="preview-page">
      <header className="preview-header">
        <button
          type="button"
          className="preview-back-btn"
          aria-label="Go back"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft />
        </button>
        <h1 className="preview-page-title">Preview</h1>
      </header>

      <main className="preview-sheet">
        <p className="preview-idea-title">{title}</p>

        <div className="preview-photo">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="Idea preview"
              className="preview-image"
              onClick={() => setShowPopup(true)}
              onError={() => setUseFirst(false)}
            />
          ) : (
            <div className="preview-placeholder">Loading image…</div>
          )}
        </div>

        <div className="preview-tags">
          {tags.map((tag) => (
            <span key={tag} className="preview-tag">{tag}</span>
          ))}
        </div>

        <p className="preview-description">{description}</p>

        <button
          className="preview-register-btn"
          onClick={handleSubmit}
          disabled={posting}
        >
          Enter & Compete
        </button>

        {posting && (
          <div className="preview-payment-overlay">
            <div className="preview-spinner"></div>
            <h3>Submitting your idea…</h3>
            <p>Please don't close this window.</p>
          </div>
        )}
      </main>

      {showPopup && (
        <div className="preview-zoom-overlay">
          <button
            type="button"
            className="preview-zoom-close"
            aria-label="Close image"
            onClick={() => setShowPopup(false)}
          >
            &times;
          </button>
          <div className="preview-zoom-frame">
            <Zoom>
              <img
                src={previewSrc}
                alt="Zoomed idea preview"
                className="preview-zoom-img"
              />
            </Zoom>
          </div>
        </div>
      )}

      <RestrictionModal
        open={!!restriction}
        variant={restriction?.variant}
        stampText={restriction?.stampText}
        message={restriction?.message}
        onClose={() => setRestriction(null)}
      />

      <BottomNav />
    </div>
  );
};

export default PreviewCompetitionPage;